import {
  CLOSED_STATUSES,
  COMPLETED_STATUSES,
  OPEN_STATUSES,
  PROCESSING_STATUSES,
  CANCELED_STATUSES,
} from '../constants';

// Statuses that allow profile change: Open, Completed, Closed, or Canceled
const PROFILE_CHANGE_ALLOWED_STATUSES = [
  ...OPEN_STATUSES,
  ...COMPLETED_STATUSES,
  ...CLOSED_STATUSES,
  ...CANCELED_STATUSES,
];

/**
 * Checks if all patient statuses are in the provided array of statuses
 * @param counts - Record of status counts from patient.organizationStatus.counts
 * @param statusArray - Array of statuses to check against
 * @returns true if all active statuses are in the provided array, false otherwise
 */
export const areAllStatusesInArray = (
  counts: Record<string, number>,
  statusArray: string[],
): boolean => {
  const activeStatuses = Object.keys(counts).filter(
    (status) => (counts[status] ?? 0) > 0,
  );

  return activeStatuses.every((status) => statusArray.includes(status));
};

export const areAllRequestsOpen = (counts: Record<string, number>): boolean => {
  return areAllStatusesInArray(counts, OPEN_STATUSES);
};

export const areAllRequestsProcessing = (
  counts: Record<string, number>,
): boolean => {
  return areAllStatusesInArray(counts, PROCESSING_STATUSES);
};

export const areAllRequestsClosed = (
  counts: Record<string, number>,
): boolean => {
  return areAllStatusesInArray(counts, CLOSED_STATUSES);
};

export const areAllRequestsCanceled = (
  counts: Record<string, number>,
): boolean => {
  return areAllStatusesInArray(counts, CANCELED_STATUSES);
};

export const areAllRequestsCompleted = (
  counts: Record<string, number>,
): boolean => {
  return areAllStatusesInArray(counts, COMPLETED_STATUSES);
};

export const hasCombinationOfStatuses = (
  counts: Record<string, number>,
): boolean => {
  return (
    !areAllRequestsOpen(counts) &&
    !areAllRequestsProcessing(counts) &&
    !areAllRequestsClosed(counts) &&
    !areAllRequestsCanceled(counts) &&
    !areAllRequestsCompleted(counts)
  );
};

/**
 * Checks if profile change is allowed for a patient based on their request statuses.
 * Profile change is only allowed if ALL requests have status groups in:
 * Open, Completed, Closed, or Canceled.
 * If there's even one request with a status NOT in these groups (e.g., Processing),
 * profile change should be disabled.
 */
export const isProfileChangeAllowed = (
  counts: Record<string, number>,
): boolean => {
  return areAllStatusesInArray(counts, PROFILE_CHANGE_ALLOWED_STATUSES);
};
